// Pure helpers for the sidebar chat list: recency buckets, filtering and
// the visible order used for shift-click range selection.

import type { ChatSession } from '../types';

export type RecencyBucket = 'today' | 'yesterday' | 'week' | 'month' | 'older';

export const RECENCY_ORDER: RecencyBucket[] = ['today', 'yesterday', 'week', 'month', 'older'];

export const RECENCY_LABEL: Record<RecencyBucket, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  week: 'Last 7 days',
  month: 'Last 30 days',
  older: 'Older',
};

const DAY_MS = 86_400_000;

function startOfDay(ts: number): number {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Calendar-day bucket of `ts` relative to `now` (local time). Future
 *  timestamps (clock skew between server and browser) count as today. */
export function recencyBucket(ts: number, now: number = Date.now()): RecencyBucket {
  const today = startOfDay(now);
  if (ts >= today) return 'today';
  if (ts >= today - DAY_MS) return 'yesterday';
  if (ts >= today - 7 * DAY_MS) return 'week';
  if (ts >= today - 30 * DAY_MS) return 'month';
  return 'older';
}

/** Group sessions (already filtered) by recency, newest first inside each
 *  bucket. Empty buckets are omitted. */
export function groupByRecency(
  sessions: ChatSession[],
  now: number = Date.now(),
): { bucket: RecencyBucket; sessions: ChatSession[] }[] {
  const map = new Map<RecencyBucket, ChatSession[]>();
  for (const s of sessions) {
    const b = recencyBucket(s.updatedAt, now);
    const arr = map.get(b) ?? [];
    arr.push(s);
    map.set(b, arr);
  }
  const out: { bucket: RecencyBucket; sessions: ChatSession[] }[] = [];
  for (const b of RECENCY_ORDER) {
    const arr = map.get(b);
    if (arr && arr.length) {
      arr.sort((a, z) => z.updatedAt - a.updatedAt);
      out.push({ bucket: b, sessions: arr });
    }
  }
  return out;
}

/** Case-insensitive filter. Matches the title, or any of the extra texts
 *  (message bodies the caller already has in memory). */
export function matchesFilter(title: string, needle: string, extraTexts?: Iterable<string>): boolean {
  const q = needle.trim().toLowerCase();
  if (!q) return true;
  if (title.toLowerCase().includes(q)) return true;
  if (extraTexts) {
    for (const t of extraTexts) {
      if (t && t.toLowerCase().includes(q)) return true;
    }
  }
  return false;
}

/** Ids between `a` and `b` (inclusive) in `order`. If either id is not in
 *  the order, returns just `[b]`. Duplicate ids in `order` are tolerated
 *  (first occurrence wins). */
export function rangeBetween(order: string[], a: string, b: string): string[] {
  const ia = order.indexOf(a);
  const ib = order.indexOf(b);
  if (ia < 0 || ib < 0) return [b];
  const [lo, hi] = ia <= ib ? [ia, ib] : [ib, ia];
  return Array.from(new Set(order.slice(lo, hi + 1)));
}
